/**
 * MER-688 / MER-690: the client dashboard nav, stripped to the essentials.
 *
 * AJ 2026-09-14: "Hide: Data Quality (only seen to staff when
 * impersonating), move the pro+ to where 'Dashboard' is now so it would
 * be 'Dashboard Pro+', hide benchmarking, financial health, sales
 * pipeline, Insights and findings." Both tenants get the same shell.
 *
 * AJ 2026-09-15 (MER-690), with the target order in hand: one
 * "Dashboard Pro+" section that reads the same for every session, and an
 * "Internal" block, staff only, that holds everything a client never
 * sees: Insights, Findings, and the manifest tabs flagged staff_only.
 * Before MER-690 a staff session saw the staff_only tabs inline in the
 * client section, so staff could not tell what a client actually gets.
 *
 * This module owns the section math so node:test can exercise it without
 * React (the nowCutoff.mjs / flagMap.mjs precedent). DashboardShell.tsx
 * renders what comes back and nothing else decides what a client sees in
 * the sidebar. The Executive link (MER-674) is not a manifest tab; the
 * shell places it first inside the "Dashboard Pro+" section on its own
 * exec_kpi probe.
 *
 * Two independent gates apply to a tab:
 *   - tier (ManifestTab.tier vs the customer tier) -> a LOCK BADGE. The tab
 *     stays in the nav; the page shows the upgrade notice. Unchanged here.
 *   - staff_only (ManifestTab.staff_only) -> the tab is ABSENT from the
 *     client section for every session and listed under Internal for a
 *     staff session. A UI gate on top of RLS, never a data gate: the
 *     manifest is a public file, so hiding a tab hides nothing a client
 *     could not already read.
 *
 * The Dashboard and Pro+ sections that used to be separate collapse into
 * one "Dashboard Pro+" section: the former dashboard tabs first, the former
 * Pro+ tabs after, in manifest order within each group. That is exactly the
 * order the shell rendered before, minus the second header.
 */

/**
 * The tabs that used to sit under the "Pro+" header (MER-159 sub-4). They
 * still render after the everyday operator tabs; only the header is gone.
 * Membership is independent of tier: some tier-locked tabs (Billing &
 * Collections, Safety & Fleet, Maintenance, Profitability) belong in the
 * operator's primary list and never lived here.
 */
export const PRO_PLUS_TAB_IDS = new Set([
  "Benchmarking",
  "Financial Health",
  "Sales Pipeline",
  "Executive Roll-Up",
]);

/** The single section title a client sees above the tab list. */
export const NAV_SECTION_TITLE = "Dashboard Pro+";

/** The staff-only block title (Insights, Findings, the staff_only tabs). */
export const INTERNAL_SECTION_TITLE = "Internal";

function tabKey(tab) {
  return tab.id ?? tab.label ?? "";
}

/**
 * Build the sidebar sections for a session.
 *
 * @param {Array<{id?: string, label?: string, staff_only?: boolean}>} tabs
 *   Manifest tabs in manifest order. A tab with neither id nor label is
 *   dropped (it has no route and no label to render).
 * @param {{ isStaff?: boolean }} [opts]
 *   isStaff: true for a staff session, impersonating or not. Only a strict
 *   boolean true counts; anything else is a client.
 * @returns {{ sections: Array<{title: string, tabs: object[]}>, internalTabs: object[], showInternal: boolean }}
 *   sections: always exactly one, titled "Dashboard Pro+", holding the
 *     tabs a CLIENT sees, for every session.
 *   internalTabs: the staff_only tabs in manifest order for a staff
 *     session, empty for a client. The shell lists them under Internal
 *     after Insights and Findings.
 *   showInternal: whether the shell renders the Internal block at all.
 *     Equal to isStaff.
 */
export function buildNavSections(tabs, opts = {}) {
  const staff = opts.isStaff === true;
  const named = (tabs ?? []).filter((t) => t && tabKey(t));
  const client = named.filter((t) => t.staff_only !== true);
  const dashboard = client.filter((t) => !PRO_PLUS_TAB_IDS.has(tabKey(t)));
  const proPlus = client.filter((t) => PRO_PLUS_TAB_IDS.has(tabKey(t)));
  return {
    sections: [{ title: NAV_SECTION_TITLE, tabs: [...dashboard, ...proPlus] }],
    internalTabs: staff ? named.filter((t) => t.staff_only === true) : [],
    showInternal: staff,
  };
}

/**
 * The tab ids a session may open or pick as its landing tab, in nav order:
 * the client section first, then (staff only) the Internal tabs. Same gate
 * as buildNavSections (a client never gets a staff_only id), so the
 * Settings "Default landing tab" options and the CustomerDashboard
 * landing-tab guard cannot disagree with the sidebar.
 *
 * @param {Array<{id?: string, label?: string, staff_only?: boolean}>} tabs
 * @param {{ isStaff?: boolean }} [opts]
 * @returns {string[]}
 */
export function visibleTabIds(tabs, opts = {}) {
  const nav = buildNavSections(tabs, opts);
  return [...nav.sections.flatMap((s) => s.tabs.map(tabKey)), ...nav.internalTabs.map(tabKey)];
}

/**
 * Resolve the tab a session lands on: the persisted preference when it
 * names a tab this session may see, else the first visible tab, else
 * undefined (no tabs at all). A preference left over from before a tab
 * went staff_only, or saved by a staff user on a shared browser, falls
 * back instead of redirecting a client to the staff-only wall.
 *
 * @param {Array<{id?: string, label?: string, staff_only?: boolean}>} tabs
 * @param {string | null | undefined} preferred
 * @param {{ isStaff?: boolean }} [opts]
 * @returns {string | undefined}
 */
export function resolveLandingTab(tabs, preferred, opts = {}) {
  const visible = visibleTabIds(tabs, opts);
  return preferred && visible.includes(preferred) ? preferred : visible[0];
}

/** The Executive board route (MER-674): not a manifest tab, placed first by the shell. */
export const EXECUTIVE_ROUTE = "Executive";

/**
 * AJ 2026-09-28 (Best Disposal's Board shown as its Executive tab): the id of
 * the manifest tab a customer override marked `executive: true`, when this
 * session may see it; else undefined. The template never sets the flag, so
 * only a tenant whose dashboard_overrides.yaml opts in ever gets one.
 *
 * When there is one it IS the tenant's Executive entry: the shell does not
 * render the separate workbook link (MER-674), the bare /<slug> URL lands on
 * it, and /<slug>/Executive redirects to it. That is the rule that keeps a
 * tenant from ever showing two entries called Executive, even if it later
 * gains exec_kpi rows.
 *
 * @param {Array<{id?: string, label?: string, staff_only?: boolean, executive?: boolean}>} tabs
 * @param {{ isStaff?: boolean }} [opts]
 * @returns {string | undefined}
 */
export function executiveTabId(tabs, opts = {}) {
  const visible = new Set(visibleTabIds(tabs, opts));
  const tab = (tabs ?? []).find((t) => t && t.executive === true && visible.has(tabKey(t)));
  return tab ? tabKey(tab) : undefined;
}

/**
 * Wave 1 release (AJ 2026-09-28): where the bare /<slug> URL sends a
 * session. A tab marked `executive: true` (executiveTabId) is the landing
 * page when present. Otherwise Executive is the landing page when the tenant has the workbook
 * (the exec_kpi probe said "available"), the same condition the shell uses
 * to show the Executive link first in the nav. Otherwise the first tab the
 * session may see (resolveLandingTab), which is the first wave 1 tab for a
 * client. A saved Settings preference that names a visible tab still wins.
 *
 * @param {Array<{id?: string, label?: string, staff_only?: boolean}>} tabs
 * @param {string | null | undefined} preferred
 * @param {"pending" | "available" | "empty" | "missing" | "error"} execAvailability
 * @param {{ isStaff?: boolean, derivedExecutive?: boolean }} [opts]
 * @returns {string | null | undefined} null while the exec probe is pending
 *   (the caller waits); undefined when the manifest has no visible tab.
 */
export function resolveIndexRoute(tabs, preferred, execAvailability, opts = {}) {
  const visible = visibleTabIds(tabs, opts);
  if (preferred && visible.includes(preferred)) return preferred;
  // A manifest tab marked as this customer's Executive view wins over the
  // workbook page, whatever the exec_kpi probe says (and without waiting on it).
  const execTab = executiveTabId(tabs, opts);
  if (execTab) return execTab;
  // AJ 2026-09-28: a tenant whose override sets `executive_page: derived`
  // lands on the Executive page (the Meridian-derived board) without waiting
  // on the workbook probe, which it does not use.
  if (opts.derivedExecutive === true) return EXECUTIVE_ROUTE;
  if (execAvailability === "available") return EXECUTIVE_ROUTE;
  if (execAvailability === "pending") return null;
  return visible[0];
}
